"use client";

import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import "./ArticleBodyEditor.css";

const ArticleBodyEditor = forwardRef(function ArticleBodyEditor(
  {
    value = "",
    onChange,
    disabled = false,
    /** Upload image via WordPress media; return public URL */
    onUploadImage,
  },
  ref
) {
  const fileInputRef = useRef(null);
  const isSyncingRef = useRef(false);

  const editor = useEditor({
    // Next.js renders client components on the server too.
    // TipTap supports SSR by deferring the first render.
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({
        placeholder: "ابدأ بكتابة المقال…",
      }),
    ],
    content: value && value.trim() ? value : "<p></p>",
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "article-body-editor-content",
        dir: "rtl",
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (isSyncingRef.current) return;
      onChange?.(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  /** Parent clears `value`; keep editor HTML in sync */
  useEffect(() => {
    if (!editor || value === undefined) return;
    const current = editor.getHTML();
    const emptyCurrent =
      current === "<p></p>" || current === '<p dir="rtl"></p>' || current === "";
    if (!value.trim() && !emptyCurrent) {
      isSyncingRef.current = true;
      editor.commands.clearContent(true);
      isSyncingRef.current = false;
      return;
    }
    if (value.trim() && value.trim() !== current.trim()) {
      isSyncingRef.current = true;
      editor.commands.setContent(value || "<p></p>", false);
      isSyncingRef.current = false;
    }
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      insertImage(src) {
        if (!editor?.isEditable) return;
        editor.chain().focus().setImage({ src }).run();
      },
      /** Force empty document; parent should also `setContent("")`. */
      clear() {
        if (!editor) return;
        isSyncingRef.current = true;
        editor.chain().focus().clearContent(true).run();
        isSyncingRef.current = false;
      },
    }),
    [editor]
  );

  const runImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file || !onUploadImage || !editor) return;
    try {
      const url = await onUploadImage(file);
      if (url) editor.chain().focus().setImage({ src: url }).run();
    } catch (_) {
      // Parent should toast errors
    }
  };

  const addLink = () => {
    if (!editor?.isEditable) return;
    const prev = editor.getAttributes("link").href;
    const raw = typeof window !== "undefined" ? window.prompt("أدخل الرابط:", prev || "https://") : null;
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
  };

  if (!editor) {
    return (
      <div className="article-body-editor border rounded p-3 text-muted" dir="rtl">
        جاري تحميل المحرر…
      </div>
    );
  }

  return (
    <div className="article-body-editor border rounded" dir="rtl">
      <div className="p-2 border-bottom bg-light d-flex flex-wrap gap-1 align-items-center">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="عريض"
          disabled={!editor.can().chain().focus().toggleBold().run()}
        >
          <i className="bi bi-type-bold" aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="مائل"
          disabled={!editor.can().chain().focus().toggleItalic().run()}
        >
          <i className="bi bi-type-italic" aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="تحت خط"
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
        >
          <i className="bi bi-type-underline" aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="قائمة نقطية"
        >
          <i className="bi bi-list-ul" aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="قائمة مرقمة"
        >
          <i className="bi bi-list-ol" aria-hidden />
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addLink} title="رابط">
          <i className="bi bi-link-45deg" aria-hidden />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => fileInputRef.current?.click()}
          title="رفع وإدراج صورة"
          disabled={!onUploadImage || disabled}
        >
          <i className="bi bi-image" aria-hidden />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp"
          className="d-none"
          onChange={runImagePick}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default ArticleBodyEditor;
